import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Wallet, Plus, Search, Download, TrendingUp, TrendingDown, AlertTriangle,
  Loader2, X, CheckCircle2, Clock, Ban, DollarSign, ArrowUpRight, ArrowDownRight,
  ScrollText, Sparkles, Zap, Crown, ChevronRight, FileText, Trash2, Pencil, Eye,
  Receipt, CalendarDays,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  Legend, PieChart, Pie, Cell,
} from "recharts";

const ESTADO_META = {
  pendiente: { label: "Pendiente", icon: Clock,       dot: "#B88537", bg: "#FBEED4", fg: "#7A5312", border: "#E5C892" },
  parcial:   { label: "Parcial",   icon: TrendingUp,  dot: "#4A6D8C", bg: "#DDE9F3", fg: "#2C4A63", border: "#8FB0CB" },
  pagado:    { label: "Pagado",    icon: CheckCircle2, dot: "#16A34A", bg: "#D8EFDD", fg: "#155724", border: "#7BC48A" },
  anulado:   { label: "Anulado",   icon: Ban,         dot: "#6B7280", bg: "#F3F4F6", fg: "#374151", border: "#9CA3AF" },
};

const METODOS = [
  { value: "efectivo", label: "Efectivo" },
  { value: "transferencia", label: "Transferencia" },
  { value: "sinpe", label: "SINPE Móvil" },
  { value: "tarjeta", label: "Tarjeta" },
  { value: "cheque", label: "Cheque" },
  { value: "otro", label: "Otro" },
];

const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";

function fmtMoney(v, moneda = "CRC") {
  const symbol = moneda === "USD" ? "$" : "₡";
  const n = Number(v || 0);
  try {
    return `${symbol} ${n.toLocaleString("es-CR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  } catch { return `${symbol} ${n}`; }
}

function fmtMoneyFull(v, moneda = "CRC") {
  const symbol = moneda === "USD" ? "$" : "₡";
  const n = Number(v || 0);
  try {
    return `${symbol} ${n.toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } catch { return `${symbol} ${n}`; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y) return iso;
  return `${d}/${m}/${y}`;
}

function EstadoBadge({ estado }) {
  const m = ESTADO_META[estado] || ESTADO_META.pendiente;
  const Icon = m.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border"
      style={{ backgroundColor: m.bg, color: m.fg, borderColor: m.border }}
    >
      <Icon className="h-3 w-3" strokeWidth={2.2} /> {m.label}
    </span>
  );
}

function KpiCard({ title, value, moneda, icon: Icon, tint, subtitle, delta, testId }) {
  const positive = delta != null && delta >= 0;
  return (
    <div
      data-testid={testId}
      className="rounded-xl bg-white border border-[#E5DFD3] p-5 flex flex-col gap-3 hover:border-[#C25A5A] transition-colors relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-[0.06]"
        style={{ backgroundColor: tint }}
      />
      <div className="flex items-center justify-between relative">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B5D57] font-semibold">{title}</div>
        <div
          className="h-8 w-8 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${tint}14`, border: `1px solid ${tint}30` }}
        >
          <Icon className="h-4 w-4" strokeWidth={1.8} style={{ color: tint }} />
        </div>
      </div>
      <div className="relative">
        <div className="font-display text-2xl text-[#1A0810] leading-none">{fmtMoney(value, moneda)}</div>
        <div className="mt-1.5 flex items-center gap-2 text-[11px]">
          {subtitle && <span className="text-[#6B5D57]">{subtitle}</span>}
          {delta != null && (
            <span className={`inline-flex items-center gap-0.5 font-semibold ${positive ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
              {positive ? <ArrowUpRight className="h-3 w-3" strokeWidth={2.5}/> : <ArrowDownRight className="h-3 w-3" strokeWidth={2.5}/>}
              {Math.abs(delta)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function AgingBar({ aging, moneda }) {
  const total = ["al_dia","1_30","31_60","61_90","90_plus"].reduce((s,k)=>s+(aging?.[k]||0),0);
  const segs = [
    { key: "al_dia",  label: "Al día",     color: "#16A34A", value: aging?.al_dia||0 },
    { key: "1_30",    label: "1-30 días",  color: "#B88537", value: aging?.["1_30"]||0 },
    { key: "31_60",   label: "31-60 días", color: "#D97706", value: aging?.["31_60"]||0 },
    { key: "61_90",   label: "61-90 días", color: "#DC2626", value: aging?.["61_90"]||0 },
    { key: "90_plus", label: "90+ días",   color: "#7f1d1d", value: aging?.["90_plus"]||0 },
  ];
  return (
    <div className="rounded-xl bg-white border border-[#E5DFD3] p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B5D57] font-semibold">Antigüedad de saldos</div>
          <div className="mt-1 font-display text-lg text-[#1A0810]">Aging report · {moneda}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-[#6B5D57]">Total pendiente</div>
          <div className="font-display text-xl text-[#7A1E2E]">{fmtMoney(total, moneda)}</div>
        </div>
      </div>
      <div className="mt-4 h-4 rounded-full overflow-hidden flex bg-[#F5EEE0]">
        {total > 0 && segs.map((s) => (
          s.value > 0 && (
            <div
              key={s.key}
              title={`${s.label}: ${fmtMoney(s.value, moneda)}`}
              style={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color }}
            />
          )
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
        {segs.map((s) => (
          <div key={s.key} className="rounded-lg border border-[#E5DFD3] p-2">
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
              <span className="text-[10px] uppercase tracking-wider text-[#6B5D57] font-semibold">{s.label}</span>
            </div>
            <div className="mt-1 text-sm font-semibold text-[#1A0810]">{fmtMoney(s.value, moneda)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Cobros() {
  const { user } = useAuth();
  const [monedaView, setMonedaView] = useState("CRC");

  const [config, setConfig] = useState({ estados: [], metodos_pago: [], monedas: ["CRC","USD"] });
  const [clientes, setClientes] = useState([]);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [aging, setAging] = useState(null);
  const [monthly, setMonthly] = useState({ meses: [] });
  const [topDeudores, setTopDeudores] = useState([]);
  const [cobrables, setCobrables] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("todos");
  const [clienteFilter, setClienteFilter] = useState("todos");
  const [rangoDesde, setRangoDesde] = useState("");
  const [rangoHasta, setRangoHasta] = useState("");
  const [soloVencidos, setSoloVencidos] = useState(false);

  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ cliente_id: "", concepto: "", monto: "", moneda: "CRC", fecha: new Date().toISOString().slice(0,10), vencimiento: "", factura: "", notas: "" });

  const [payDialog, setPayDialog] = useState(null); // cobro
  const [payForm, setPayForm] = useState({ monto_pagado: "", metodo_pago: "efectivo", referencia: "", fecha_pago: new Date().toISOString().slice(0,10), nota: "" });

  const [detailDialog, setDetailDialog] = useState(null);
  const [importDialog, setImportDialog] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportRange, setExportRange] = useState({ desde: "", hasta: "", estado: "todos", moneda: "todos" });

  const loadAll = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (estadoFilter !== "todos") params.estado = estadoFilter;
    if (clienteFilter !== "todos") params.cliente_id = clienteFilter;
    if (rangoDesde) params.desde = rangoDesde;
    if (rangoHasta) params.hasta = rangoHasta;
    if (soloVencidos) params.vencidos = true;
    const [lst, st, ag, mo, td] = await Promise.all([
      api.get("/cobros", { params }),
      api.get("/cobros/stats"),
      api.get("/cobros/aging", { params: { moneda: monedaView } }),
      api.get("/cobros/monthly", { params: { moneda: monedaView, meses: 6 } }),
      api.get("/cobros/top-deudores", { params: { moneda: monedaView, limit: 5 } }),
    ]);
    setRows(lst.data || []);
    setStats(st.data || null);
    setAging(ag.data || {});
    setMonthly(mo.data || { meses: [] });
    setTopDeudores(td.data?.rows || []);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cfg, cli, cob] = await Promise.all([
          api.get("/cobros/config"),
          api.get("/clientes"),
          api.get("/cobros/escrituras-cobrables"),
        ]);
        setConfig(cfg.data);
        setClientes(cli.data || []);
        setCobrables(cob.data || []);
      } catch (_) {}
      await loadAll();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadAll(); /* refresh on filter */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, estadoFilter, clienteFilter, rangoDesde, rangoHasta, soloVencidos, monedaView]);

  const kpi = stats?.por_moneda?.[monedaView] || { facturado: 0, cobrado: 0, pendiente: 0, vencido: 0, count: 0 };
  const delta = stats?.delta_pct?.[monedaView];

  const distEstados = useMemo(() => {
    const dist = { pendiente: 0, parcial: 0, pagado: 0, anulado: 0 };
    rows.forEach((r) => { if (dist[r.estado] != null) dist[r.estado] += 1; });
    return Object.entries(dist).map(([key, value]) => ({ name: ESTADO_META[key]?.label || key, key, value }));
  }, [rows]);

  const clienteMap = useMemo(() => Object.fromEntries((clientes || []).map((c) => [c.id, c.nombre])), [clientes]);

  const openNew = () => {
    setNewForm({ cliente_id: "", concepto: "", monto: "", moneda: monedaView, fecha: new Date().toISOString().slice(0,10), vencimiento: "", factura: "", notas: "" });
    setNewOpen(true);
  };

  const saveNew = async () => {
    if (!newForm.cliente_id || !newForm.concepto.trim() || !Number(newForm.monto)) {
      toast.error("Cliente, concepto y monto son obligatorios"); return;
    }
    try {
      await api.post("/cobros", { ...newForm, monto: Number(newForm.monto), proceso_id: null, escritura_id: null });
      toast.success("Cobro creado");
      setNewOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo crear");
    }
  };

  const openPay = (c) => {
    setPayDialog(c);
    setPayForm({
      monto_pagado: c.saldo || (c.monto || 0),
      metodo_pago: "efectivo",
      referencia: "",
      fecha_pago: new Date().toISOString().slice(0,10),
      nota: "",
    });
  };

  const registrarPago = async () => {
    if (!payDialog) return;
    if (!Number(payForm.monto_pagado) || Number(payForm.monto_pagado) <= 0) {
      toast.error("Monto inválido"); return;
    }
    try {
      await api.post(`/cobros/${payDialog.id}/pagar`, { ...payForm, monto_pagado: Number(payForm.monto_pagado) });
      toast.success("Pago registrado");
      setPayDialog(null);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo registrar");
    }
  };

  const eliminar = async (c) => {
    if (!window.confirm(`¿Eliminar el cobro "${c.concepto}"?`)) return;
    try {
      await api.delete(`/cobros/${c.id}`);
      toast.success("Cobro eliminado");
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  const importarDesdeEscritura = async (esc) => {
    try {
      await api.post(`/cobros/from-escritura/${esc.id}`);
      toast.success(`Cobro creado desde escritura N° ${esc.numero}`);
      const [cob] = await Promise.all([api.get("/cobros/escrituras-cobrables")]);
      setCobrables(cob.data || []);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo importar");
    }
  };

  const importarTodas = async () => {
    if (!cobrables.length) return;
    let ok = 0, fail = 0;
    for (const esc of cobrables) {
      try { await api.post(`/cobros/from-escritura/${esc.id}`); ok++; } catch { fail++; }
    }
    toast.success(`Importadas ${ok} escrituras${fail ? ` · ${fail} con error` : ""}`);
    const cob = await api.get("/cobros/escrituras-cobrables");
    setCobrables(cob.data || []);
    await loadAll();
    setImportDialog(false);
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (exportRange.desde) params.desde = exportRange.desde;
      if (exportRange.hasta) params.hasta = exportRange.hasta;
      if (exportRange.estado !== "todos") params.estado = exportRange.estado;
      if (exportRange.moneda !== "todos") params.moneda = exportRange.moneda;
      const res = await api.get("/cobros/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "cobros.csv"; a.click();
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (_) { toast.error("No se pudo exportar"); }
  };

  const donutColors = ["#B88537","#4A6D8C","#16A34A","#6B7280"];

  return (
    <div className="space-y-8" data-testid="module-cobros">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#C25A5A] font-semibold">Módulo</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl text-[#1A0810]">Cobros</h1>
          <p className="mt-2 text-sm text-[#4a3838] max-w-2xl">
            Honorarios, facturación y estado de cuenta del bufete. Todo el flujo financiero en un solo panel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {cobrables.length > 0 && (
            <Button
              data-testid="btn-import-notarial"
              variant="outline"
              onClick={() => setImportDialog(true)}
              className="h-10 border-[#B88537] bg-[#FFF4D1] text-[#7A5312] hover:bg-[#FCE9A0]"
            >
              <Sparkles className="h-4 w-4 mr-2" strokeWidth={2} />
              Importar {cobrables.length} de Notarial
            </Button>
          )}
          <Button variant="outline" data-testid="btn-export-cobros" onClick={() => setExportOpen(true)} className="h-10 border-[#E5DFD3] text-[#3d2a2a] hover:bg-[#F5EEE0]">
            <Download className="h-4 w-4 mr-2" strokeWidth={1.8}/> Exportar CSV
          </Button>
          <Button data-testid="btn-nuevo-cobro" onClick={openNew} className="h-10 bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]">
            <Plus className="h-4 w-4 mr-2" strokeWidth={2}/> Nuevo cobro
          </Button>
        </div>
      </div>

      {/* Moneda tabs */}
      <div className="inline-flex rounded-full bg-[#F5EEE0] p-1 border border-[#E5DFD3]">
        {["CRC","USD"].map((m) => (
          <button
            key={m}
            data-testid={`tab-moneda-${m}`}
            onClick={() => setMonedaView(m)}
            className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full transition-colors ${
              monedaView === m ? "bg-[#7A1E2E] text-[#F1EDE4] shadow-sm" : "text-[#6B5D57] hover:text-[#7A1E2E]"
            }`}
          >
            {m === "CRC" ? "₡ Colones" : "$ Dólares"}
          </button>
        ))}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-facturado" title="Facturado" value={kpi.facturado} moneda={monedaView} icon={Receipt} tint="#7A1E2E" subtitle={`${kpi.count} cobros`}/>
        <KpiCard testId="kpi-cobrado" title="Cobrado" value={kpi.cobrado} moneda={monedaView} icon={CheckCircle2} tint="#16A34A" subtitle="Todos los pagos" delta={delta}/>
        <KpiCard testId="kpi-pendiente" title="Pendiente" value={kpi.pendiente} moneda={monedaView} icon={Clock} tint="#D97706" subtitle="Por cobrar"/>
        <KpiCard testId="kpi-vencido" title="Vencido" value={kpi.vencido} moneda={monedaView} icon={AlertTriangle} tint="#DC2626" subtitle="Requiere gestión"/>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl bg-white border border-[#E5DFD3] p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B5D57] font-semibold">Últimos 6 meses</div>
              <div className="mt-1 font-display text-lg text-[#1A0810]">Facturado vs Cobrado</div>
            </div>
            <div className="text-[11px] flex items-center gap-3 text-[#6B5D57]">
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#7A1E2E]"/> Facturado</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#16A34A]"/> Cobrado</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthly.meses || []} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5DFD3" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B5D57" }} />
              <YAxis tick={{ fontSize: 11, fill: "#6B5D57" }} tickFormatter={(v)=> v>=1000000?`${(v/1000000).toFixed(1)}M`: v>=1000?`${(v/1000).toFixed(0)}K`:v} />
              <Tooltip
                formatter={(v)=>fmtMoney(v, monedaView)}
                contentStyle={{ background:"#FBF9F4", border:"1px solid #E5DFD3", borderRadius:8, fontSize:12 }}
              />
              <Bar dataKey="facturado" fill="#7A1E2E" radius={[4,4,0,0]} />
              <Bar dataKey="cobrado" fill="#16A34A" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl bg-white border border-[#E5DFD3] p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B5D57] font-semibold">Distribución</div>
          <div className="mt-1 font-display text-lg text-[#1A0810]">Cobros por estado</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={distEstados}
                dataKey="value"
                nameKey="name"
                cx="50%" cy="50%"
                innerRadius={40} outerRadius={70}
                paddingAngle={2}
              >
                {distEstados.map((entry, index) => (
                  <Cell key={entry.key} fill={donutColors[index % donutColors.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background:"#FBF9F4", border:"1px solid #E5DFD3", borderRadius:8, fontSize:12 }}/>
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1">
            {distEstados.map((d, i) => (
              <div key={d.key} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 text-[#3d2a2a]">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: donutColors[i] }}/>
                  {d.name}
                </span>
                <span className="font-semibold">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Aging + Top deudores */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <AgingBar aging={aging} moneda={monedaView} />
        </div>
        <div className="rounded-xl bg-white border border-[#E5DFD3] p-5">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B5D57] font-semibold">Top deudores</div>
              <div className="mt-1 font-display text-lg text-[#1A0810]">Saldo pendiente · {monedaView}</div>
            </div>
            <Crown className="h-4 w-4 text-[#B88537]" fill="#B88537" strokeWidth={2}/>
          </div>
          <div className="mt-2 space-y-2">
            {topDeudores.length === 0 && <div className="text-xs text-[#6B5D57] py-4 text-center">Sin deudas · ¡Excelente!</div>}
            {topDeudores.map((d, i) => (
              <div
                key={d.cliente_id}
                data-testid={`top-deudor-${i}`}
                className="rounded-lg border border-[#E5DFD3] p-3 hover:bg-[#FBF9F4] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#7A1E2E] to-[#B88537] text-white flex items-center justify-center text-[11px] font-semibold flex-shrink-0">
                      {i+1}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#1A0810] truncate">{d.cliente_nombre || "Sin nombre"}</div>
                      <div className="text-[11px] text-[#6B5D57]">{d.cobros} cobro{d.cobros!==1?"s":""} pendiente{d.cobros!==1?"s":""}</div>
                    </div>
                  </div>
                  <div className="text-right ml-2">
                    <div className="text-sm font-semibold text-[#7A1E2E]">{fmtMoney(d.saldo, monedaView)}</div>
                    {d.vencido_saldo > 0 && (
                      <div className="text-[10px] text-[#DC2626] font-semibold uppercase tracking-wider">
                        {fmtMoney(d.vencido_saldo, monedaView)} vencido
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl bg-white border border-[#E5DFD3] p-4 flex flex-col md:flex-row gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8}/>
          <Input data-testid="input-search-cobros" placeholder="Buscar concepto, factura, notas…" value={q} onChange={(e)=>setQ(e.target.value)} className="h-11 pl-10 bg-white border-[#E5DFD3]"/>
        </div>
        <Select value={estadoFilter} onValueChange={setEstadoFilter}>
          <SelectTrigger data-testid="filter-estado-cobros" className="h-11 w-full md:w-[160px] bg-white border-[#E5DFD3]"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            {config.estados?.map((e)=><SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={clienteFilter} onValueChange={setClienteFilter}>
          <SelectTrigger data-testid="filter-cliente-cobros" className="h-11 w-full md:w-[220px] bg-white border-[#E5DFD3]"><SelectValue/></SelectTrigger>
          <SelectContent className="max-h-[280px]">
            <SelectItem value="todos">Todos los clientes</SelectItem>
            {clientes.map((c)=><SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={rangoDesde} onChange={(e)=>setRangoDesde(e.target.value)} className="h-11 w-full md:w-[150px] bg-white border-[#E5DFD3]" placeholder="Desde"/>
        <Input type="date" value={rangoHasta} onChange={(e)=>setRangoHasta(e.target.value)} className="h-11 w-full md:w-[150px] bg-white border-[#E5DFD3]" placeholder="Hasta"/>
        <button
          data-testid="toggle-vencidos"
          onClick={()=>setSoloVencidos(!soloVencidos)}
          className={`h-11 px-3 rounded-md border text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-1.5 ${
            soloVencidos ? "bg-[#FADCDC] border-[#EAA0A0] text-[#7A1F1F]" : "bg-white border-[#E5DFD3] text-[#6B5D57] hover:border-[#DC2626] hover:text-[#DC2626]"
          }`}
        >
          <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2}/> Solo vencidos
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-white border border-[#E5DFD3] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5EEE0] border-b border-[#E5DFD3]">
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#6B5D57] font-semibold">
                <th className="px-3 py-3">Fecha</th>
                <th className="px-3 py-3">Cliente / Concepto</th>
                <th className="px-3 py-3 w-28">Vence</th>
                <th className="px-3 py-3 w-32 text-right">Monto</th>
                <th className="px-3 py-3 w-32 text-right">Saldo</th>
                <th className="px-3 py-3 w-36">Estado</th>
                <th className="px-3 py-3 w-36 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-8 text-center text-[#6B5D57]"><Loader2 className="h-5 w-5 mx-auto animate-spin"/></td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="p-12 text-center">
                  <Wallet className="h-8 w-8 mx-auto text-[#C25A5A]" strokeWidth={1.5}/>
                  <div className="mt-3 font-display text-lg text-[#1A0810]">Sin cobros para mostrar</div>
                  <div className="mt-1 text-xs text-[#6B5D57]">Ajusta los filtros o crea un nuevo cobro.</div>
                </td></tr>
              )}
              {!loading && rows.map((c) => {
                const meta = ESTADO_META[c.estado] || ESTADO_META.pendiente;
                const isVenc = !!c.vencido;
                return (
                  <tr
                    key={c.id}
                    data-testid={`cobro-row-${c.id}`}
                    className="border-b border-[#F0EAE0] last:border-b-0 hover:bg-[#FAF7F0] transition-colors"
                    style={{ borderLeft: `3px solid ${isVenc ? "#DC2626" : meta.dot}` }}
                  >
                    <td className="px-3 py-3 text-[#3d2a2a] text-xs">{fmtDate(c.fecha)}</td>
                    <td className="px-3 py-3">
                      <div className="text-sm font-semibold text-[#1A0810]">{c.cliente_nombre || clienteMap[c.cliente_id] || "—"}</div>
                      <div className="text-xs text-[#6B5D57]">{c.concepto}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {c.factura && <span className="text-[10px] bg-[#F5EEE0] border border-[#E5DFD3] px-1.5 py-0.5 rounded text-[#6B5D57]">#{c.factura}</span>}
                        {c.escritura && <span className="text-[10px] bg-[#EDE7F6] border border-[#B39DDB] px-1.5 py-0.5 rounded text-[#4527A0]">📜 Escritura N° {c.escritura.numero}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-[#3d2a2a] text-xs">
                      {c.vencimiento ? (
                        <>
                          <div>{fmtDate(c.vencimiento)}</div>
                          {isVenc && <div className="text-[10px] text-[#DC2626] font-semibold uppercase">{c.dias_vencido}d vencido</div>}
                        </>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-3 text-right text-[#3d2a2a] font-semibold">{fmtMoney(c.monto, c.moneda)}</td>
                    <td className="px-3 py-3 text-right">
                      <div className="font-semibold" style={{ color: c.saldo > 0 ? "#7A1E2E" : "#16A34A" }}>
                        {fmtMoney(c.saldo, c.moneda)}
                      </div>
                      {c.monto_pagado > 0 && c.monto_pagado < c.monto && (
                        <div className="text-[10px] text-[#6B5D57]">{fmtMoney(c.monto_pagado, c.moneda)} pagado</div>
                      )}
                    </td>
                    <td className="px-3 py-3"><EstadoBadge estado={c.estado}/></td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {c.saldo > 0 && c.estado !== "anulado" && (
                          <button
                            data-testid={`btn-pagar-${c.id}`}
                            onClick={()=>openPay(c)}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[#16A34A] hover:bg-[#128036] text-white text-[11px] font-semibold uppercase tracking-wider"
                            title="Registrar pago"
                          >
                            <Zap className="h-3 w-3" strokeWidth={2.2}/> Pagar
                          </button>
                        )}
                        <button
                          data-testid={`btn-detail-${c.id}`}
                          onClick={()=>setDetailDialog(c)}
                          className="h-8 w-8 rounded-md hover:bg-[#F5EEE0] flex items-center justify-center text-[#6B5D57] hover:text-[#7A1E2E]"
                          title="Ver detalle">
                          <Eye className="h-4 w-4" strokeWidth={1.8}/>
                        </button>
                        <button
                          data-testid={`btn-delete-cobro-${c.id}`}
                          onClick={()=>eliminar(c)}
                          className="h-8 w-8 rounded-md hover:bg-red-50 flex items-center justify-center text-[#6B5D57] hover:text-red-700"
                          title="Eliminar">
                          <Trash2 className="h-4 w-4" strokeWidth={1.8}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Nuevo cobro */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-2xl bg-[#FAF7F0] border-[#E5DFD3] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A0810]">Nuevo cobro</DialogTitle>
            <DialogDescription className="text-[#6B5D57]">Registra un nuevo honorario o factura pendiente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Cliente *</Label>
              <Select value={newForm.cliente_id || undefined} onValueChange={(v)=>setNewForm({...newForm, cliente_id: v})}>
                <SelectTrigger data-testid="new-cliente" className={`mt-2 ${inputCls}`}><SelectValue placeholder="Selecciona…"/></SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {clientes.map((c)=><SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Concepto *</Label>
              <Input data-testid="new-concepto" value={newForm.concepto} onChange={(e)=>setNewForm({...newForm, concepto: e.target.value})} className={`mt-2 ${inputCls}`} placeholder="Honorarios por escritura…"/>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Monto *</Label>
                <Input data-testid="new-monto" type="number" min={0} step="0.01" value={newForm.monto} onChange={(e)=>setNewForm({...newForm, monto: e.target.value})} className={`mt-2 ${inputCls}`}/>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Moneda</Label>
                <Select value={newForm.moneda} onValueChange={(v)=>setNewForm({...newForm, moneda: v})}>
                  <SelectTrigger className={`mt-2 ${inputCls}`}><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CRC">₡ Colones</SelectItem>
                    <SelectItem value="USD">$ Dólares</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Factura #</Label>
                <Input value={newForm.factura} onChange={(e)=>setNewForm({...newForm, factura: e.target.value})} className={`mt-2 ${inputCls}`} placeholder="F-001"/>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Fecha de emisión</Label>
                <Input type="date" value={newForm.fecha} onChange={(e)=>setNewForm({...newForm, fecha: e.target.value})} className={`mt-2 ${inputCls}`}/>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Vencimiento</Label>
                <Input type="date" value={newForm.vencimiento} onChange={(e)=>setNewForm({...newForm, vencimiento: e.target.value})} className={`mt-2 ${inputCls}`}/>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Notas</Label>
              <Textarea value={newForm.notas} onChange={(e)=>setNewForm({...newForm, notas: e.target.value})} className="mt-2 bg-white border-[#E5DFD3]" placeholder="Detalles internos…"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setNewOpen(false)} className="h-10 border-[#E5DFD3]">Cancelar</Button>
            <Button data-testid="btn-save-cobro" onClick={saveNew} className="h-10 bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]">Registrar cobro</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Registrar pago */}
      <Dialog open={!!payDialog} onOpenChange={(v)=>!v && setPayDialog(null)}>
        <DialogContent className="max-w-lg bg-[#FAF7F0] border-[#E5DFD3]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1A0810] flex items-center gap-2">
              <Zap className="h-5 w-5 text-[#16A34A]" strokeWidth={2}/> Registrar pago
            </DialogTitle>
            <DialogDescription className="text-[#6B5D57]">
              {payDialog?.cliente_nombre} · {payDialog?.concepto}
              <div className="mt-2 rounded-md bg-white border border-[#E5DFD3] p-3 text-xs">
                <div className="flex justify-between"><span>Monto total:</span><span className="font-semibold">{fmtMoneyFull(payDialog?.monto, payDialog?.moneda)}</span></div>
                <div className="flex justify-between"><span>Ya pagado:</span><span className="font-semibold">{fmtMoneyFull(payDialog?.monto_pagado, payDialog?.moneda)}</span></div>
                <div className="flex justify-between mt-1 pt-1 border-t border-[#E5DFD3]"><span>Saldo pendiente:</span><span className="font-semibold text-[#7A1E2E]">{fmtMoneyFull(payDialog?.saldo, payDialog?.moneda)}</span></div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Monto a pagar *</Label>
              <Input data-testid="pay-monto" type="number" min="0" step="0.01" value={payForm.monto_pagado} onChange={(e)=>setPayForm({...payForm, monto_pagado: e.target.value})} className={`mt-2 ${inputCls}`}/>
              <div className="mt-1.5 flex gap-1">
                <button className="text-[10px] uppercase text-[#7A1E2E] hover:underline" onClick={()=>setPayForm({...payForm, monto_pagado: payDialog?.saldo || 0})}>Pagar todo</button>
                <span className="text-[10px] text-[#6B5D57]">·</span>
                <button className="text-[10px] uppercase text-[#7A1E2E] hover:underline" onClick={()=>setPayForm({...payForm, monto_pagado: ((payDialog?.saldo||0)/2).toFixed(2)})}>Mitad</button>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Método</Label>
              <Select value={payForm.metodo_pago} onValueChange={(v)=>setPayForm({...payForm, metodo_pago: v})}>
                <SelectTrigger data-testid="pay-metodo" className={`mt-2 ${inputCls}`}><SelectValue/></SelectTrigger>
                <SelectContent>
                  {METODOS.map((m)=><SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Referencia</Label>
                <Input value={payForm.referencia} onChange={(e)=>setPayForm({...payForm, referencia: e.target.value})} className={`mt-2 ${inputCls}`} placeholder="#Trans / #SINPE"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Fecha</Label>
                <Input type="date" value={payForm.fecha_pago} onChange={(e)=>setPayForm({...payForm, fecha_pago: e.target.value})} className={`mt-2 ${inputCls}`}/>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Nota</Label>
              <Input value={payForm.nota} onChange={(e)=>setPayForm({...payForm, nota: e.target.value})} className={`mt-2 ${inputCls}`}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPayDialog(null)} className="h-10 border-[#E5DFD3]">Cancelar</Button>
            <Button data-testid="btn-confirm-pago" onClick={registrarPago} className="h-10 bg-[#16A34A] hover:bg-[#128036] text-white">
              <Zap className="h-4 w-4 mr-2" strokeWidth={2}/> Confirmar pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalle del cobro */}
      <Dialog open={!!detailDialog} onOpenChange={(v)=>!v && setDetailDialog(null)}>
        <DialogContent className="max-w-2xl bg-[#FAF7F0] border-[#E5DFD3] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1A0810]">Detalle del cobro</DialogTitle>
          </DialogHeader>
          {detailDialog && (
            <div className="space-y-3">
              <div className="rounded-lg bg-white border border-[#E5DFD3] p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#6B5D57]">Cliente</div>
                    <div className="font-semibold text-[#1A0810]">{detailDialog.cliente_nombre}</div>
                  </div>
                  <EstadoBadge estado={detailDialog.estado}/>
                </div>
                <div className="mt-3 text-sm text-[#3d2a2a]">{detailDialog.concepto}</div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                  <div><div className="text-[10px] uppercase text-[#6B5D57]">Monto</div><div className="font-semibold">{fmtMoneyFull(detailDialog.monto, detailDialog.moneda)}</div></div>
                  <div><div className="text-[10px] uppercase text-[#6B5D57]">Pagado</div><div className="font-semibold text-[#16A34A]">{fmtMoneyFull(detailDialog.monto_pagado, detailDialog.moneda)}</div></div>
                  <div><div className="text-[10px] uppercase text-[#6B5D57]">Saldo</div><div className="font-semibold text-[#7A1E2E]">{fmtMoneyFull(detailDialog.saldo, detailDialog.moneda)}</div></div>
                </div>
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-[#6B5D57]">
                  <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3"/> Emitido {fmtDate(detailDialog.fecha)}</span>
                  {detailDialog.vencimiento && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3"/> Vence {fmtDate(detailDialog.vencimiento)}</span>}
                  {detailDialog.factura && <span>Factura #{detailDialog.factura}</span>}
                  {detailDialog.escritura && <span className="text-[#4527A0]">📜 Escritura N° {detailDialog.escritura.numero}</span>}
                </div>
                {detailDialog.notas && <div className="mt-3 text-xs text-[#3d2a2a] italic border-l-2 border-[#E5DFD3] pl-3">{detailDialog.notas}</div>}
              </div>

              <div className="rounded-lg bg-white border border-[#E5DFD3] p-4">
                <div className="text-[10px] uppercase tracking-wider text-[#6B5D57] font-semibold mb-2">Historial de pagos ({(detailDialog.pagos||[]).length})</div>
                {(detailDialog.pagos||[]).length === 0 && <div className="text-xs text-[#6B5D57]">Aún no se registran pagos.</div>}
                <div className="space-y-2">
                  {(detailDialog.pagos||[]).map((p, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-md border border-[#E5DFD3] p-3">
                      <div className="h-8 w-8 rounded-full bg-[#D8EFDD] border border-[#7BC48A] flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-[#155724]" strokeWidth={2}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#1A0810]">{fmtMoneyFull(p.monto, detailDialog.moneda)}</div>
                        <div className="text-[11px] text-[#6B5D57] flex flex-wrap gap-2">
                          <span>{fmtDate(p.fecha)}</span>
                          <span>·</span>
                          <span className="capitalize">{p.metodo}</span>
                          {p.referencia && <><span>·</span><span>Ref: {p.referencia}</span></>}
                          <span>·</span>
                          <span>{p.by}</span>
                        </div>
                        {p.nota && <div className="mt-1 text-[11px] text-[#3d2a2a] italic">{p.nota}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import desde Notarial */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent className="max-w-2xl bg-[#FAF7F0] border-[#E5DFD3] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1A0810] flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#B88537]" fill="#B88537" strokeWidth={2}/>
              Importar cobros desde Notarial
            </DialogTitle>
            <DialogDescription className="text-[#6B5D57]">
              Estas escrituras tienen cliente y monto pero aún no tienen cobro. Selecciona una o importa todas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {cobrables.length === 0 && <div className="text-sm text-[#6B5D57] py-6 text-center">No hay escrituras pendientes de facturar.</div>}
            {cobrables.map((e) => (
              <div key={e.id} data-testid={`import-esc-${e.numero}`} className="rounded-lg border border-[#E5DFD3] p-3 flex items-center justify-between gap-3 hover:bg-[#FBF9F4]">
                <div className="min-w-0">
                  <div className="font-semibold text-[#1A0810] text-sm">
                    N° {e.numero} — {e.tipo === "Otro" && e.tipo_custom ? e.tipo_custom : e.tipo}
                  </div>
                  <div className="text-xs text-[#6B5D57]">{e.cliente_nombre} · {fmtDate(e.fecha)}</div>
                </div>
                <div className="text-right flex items-center gap-3">
                  <div className="font-semibold text-[#7A1E2E]">{fmtMoney(e.monto, e.moneda)}</div>
                  <Button size="sm" onClick={()=>importarDesdeEscritura(e)} className="bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]">Importar</Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setImportDialog(false)} className="h-10 border-[#E5DFD3]">Cerrar</Button>
            {cobrables.length > 0 && (
              <Button data-testid="btn-import-all" onClick={importarTodas} className="h-10 bg-[#B88537] hover:bg-[#8A6329] text-white">
                <Sparkles className="h-4 w-4 mr-2" strokeWidth={2}/> Importar todas
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-lg bg-[#FAF7F0] border-[#E5DFD3]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1A0810]">Exportar cobros</DialogTitle>
            <DialogDescription className="text-[#6B5D57]">Filtra por rango de fechas, estado y moneda.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Desde</Label>
                <Input type="date" value={exportRange.desde} onChange={(e)=>setExportRange({...exportRange, desde: e.target.value})} className={`mt-2 ${inputCls}`}/>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Hasta</Label>
                <Input type="date" value={exportRange.hasta} onChange={(e)=>setExportRange({...exportRange, hasta: e.target.value})} className={`mt-2 ${inputCls}`}/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Estado</Label>
                <Select value={exportRange.estado} onValueChange={(v)=>setExportRange({...exportRange, estado: v})}>
                  <SelectTrigger className={`mt-2 ${inputCls}`}><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {config.estados?.map((e)=><SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Moneda</Label>
                <Select value={exportRange.moneda} onValueChange={(v)=>setExportRange({...exportRange, moneda: v})}>
                  <SelectTrigger className={`mt-2 ${inputCls}`}><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todas</SelectItem>
                    <SelectItem value="CRC">Colones</SelectItem>
                    <SelectItem value="USD">Dólares</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setExportOpen(false)} className="h-10 border-[#E5DFD3]">Cancelar</Button>
            <Button data-testid="btn-export-download-cobros" onClick={exportCsv} className="h-10 bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]">
              <Download className="h-4 w-4 mr-2"/> Descargar CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
